import {
  createRoomResponseSchema,
  healthResponseSchema,
  roomResponseSchema,
  type CreateRoomResponse,
  type ErrorCode,
  type HealthResponse,
  type RoomResponse,
} from '@flowshare/protocol';
import { isRoomCode, normalizeRoomCode } from '@flowshare/shared';

export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR';
  readonly status: number;
  readonly requestId: string | undefined;

  constructor(
    code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    status = 0,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        accept: 'application/json',
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError('NETWORK_ERROR', 'Could not reach the FlowShare service.');
  }

  const text = await response.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const error = body as
      | { error?: { code?: ErrorCode; message?: string; requestId?: string } }
      | null;
    throw new ApiError(
      error?.error?.code ?? 'INTERNAL_ERROR',
      error?.error?.message ?? 'The request failed.',
      response.status,
      error?.error?.requestId,
    );
  }

  return body;
}

export async function createRoomRequest(capacity?: number): Promise<CreateRoomResponse> {
  const body = await request('/api/rooms', {
    method: 'POST',
    body: JSON.stringify(capacity === undefined ? {} : { capacity }),
  });
  return createRoomResponseSchema.parse(body);
}

export async function fetchRoom(code: string): Promise<RoomResponse> {
  const normalized = normalizeRoomCode(code);
  if (!isRoomCode(normalized)) {
    throw new ApiError('INVALID_ROOM_CODE', 'That room code is not valid.');
  }
  const body = await request(`/api/rooms/${normalized}`);
  return roomResponseSchema.parse(body);
}

export async function closeRoomRequest(code: string, hostToken: string): Promise<void> {
  await request(`/api/rooms/${normalizeRoomCode(code)}`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${hostToken}` },
  });
}

export async function fetchHealth(): Promise<HealthResponse> {
  const body = await request('/api/health');
  return healthResponseSchema.parse(body);
}
